'use client';

import { useInfiniteQuery, useQuery } from '@tanstack/react-query';
import Link from 'next/link';
import { useMemo } from 'react';

import {
  getInfinityArticles,
  getMainArticlesQuery,
  getRubricsQuery,
} from '@/features/articles';
import { Box, Text } from '@/shared/ui';
import { Button } from '@/shared/ui/Button';
import { TextArticle } from '../TextArticle';
import { MainArticle } from './MainArticle';
import { RubricCardSkeleton } from './Skeleton';
import styles from './RubricCard.module.css';

interface RubricCardProps {
  slug: string;
}

export const RubricCard = ({ slug }: RubricCardProps) => {
  const { data: mainArticles, isLoading: mainLoading } = useQuery(
    getMainArticlesQuery({ rubric: slug, key: 'main-block' })
  );

  const { data: infinityArticles, isLoading: articlesLoading } =
    useInfiniteQuery(
      getInfinityArticles({
        rubric: slug,
        key: 'block-articles',
        limit: 15,
      })
    );

  const { data: rubrics } = useQuery(getRubricsQuery());

  const visibleMainArticles = useMemo(() => {
    return mainArticles
      ?.filter((article) => {
        return !!article.main_rubrics?.find((a) => slug === a.slug);
      })
      .slice(0, 2);
  }, [mainArticles, slug]);

  const articles = useMemo(() => {
    return infinityArticles?.pages
      .flatMap((page) => page.articles)
      ?.filter(
        (article) =>
          !visibleMainArticles?.find(
            (mainArticle) => mainArticle.id === article.id
          )
      );
  }, [infinityArticles, visibleMainArticles]);

  const rubricName = rubrics?.find((item) => item.slug === slug)?.name ?? '';

  if (
    !mainArticles?.length &&
    !articles?.length &&
    !mainLoading &&
    !articlesLoading
  )
    return null;

  if (mainLoading || articlesLoading)
    return <RubricCardSkeleton rubricName={rubricName} slug={slug} />;

  return (
    <Box
      className={styles.root}
      display="flex"
      flexDirection="column"
      padding={18}
      borderRadius={16}
    >
      <Box
        className={styles.header}
        display="flex"
        justifyContent="space-between"
        gap={12}
        paddingBottom={26}
        paddingTop={8}
        alignItems="center"
      >
        <Link href={`/rubric/${slug}`}>
          <Text
            fontSize={34}
            fontWeight={700}
            lineHeight={34}
            letterSpacing={-0.34}
            variant="secondary"
            color="black"
          >
            {rubricName}
          </Text>
        </Link>
        <Button className={styles.link} asChild>
          <Link href={`/rubric/${slug}`}>Подробнее</Link>
        </Button>
      </Box>

      <div className={styles.content}>
        {visibleMainArticles?.length ? (
          <div className={styles.main}>
            {visibleMainArticles.map((article, idx) => (
              <MainArticle
                article={article}
                rubricName={rubricName}
                key={idx}
              />
            ))}
          </div>
        ) : null}
        {articles?.length ? (
          <Box
            className={styles.articles}
            display="flex"
            flexDirection="column"
            paddingY={8}
            borderRadius={12}
          >
            {articles.map((article, idx) => (
              <TextArticle
                className={styles.article}
                article={article}
                key={idx}
              />
            ))}
          </Box>
        ) : null}
      </div>
    </Box>
  );
};
